'use client';

/**
 * Provides the current base path ("/" or "/c/{companyId}") from the URL so the app can build
 * links and navigate without handling / vs /c/{companyId} in every component. Pathname is the
 * original URL (middleware rewrites only internally), so base path stays correct.
 */
import { usePathname } from 'next/navigation';
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { getBasePath } from '@/lib/base-url-core';

type BasePathContextValue = {
  basePath: string;
  resolvePath: (path: string) => string;
};

const BasePathContext = createContext<BasePathContextValue | null>(null);

/** Builds path under base: "/" stays as-is; "/c/companyId" + path. */
function resolvePathWithBase(basePath: string, path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  if (basePath === '/') return normalized;
  const base = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;
  return `${base}${normalized}`;
}

export function BasePathProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname() ?? '';
  const basePath = useMemo(() => getBasePath(pathname), [pathname]);
  const resolvePath = useCallback(
    (path: string) => resolvePathWithBase(basePath, path),
    [basePath]
  );
  const value = useMemo<BasePathContextValue>(
    () => ({ basePath, resolvePath }),
    [basePath, resolvePath]
  );
  return <BasePathContext.Provider value={value}>{children}</BasePathContext.Provider>;
}

export function useBasePathContext(): BasePathContextValue {
  const ctx = useContext(BasePathContext);
  if (!ctx) {
    throw new Error('useBasePathContext must be used within BasePathProvider');
  }
  return ctx;
}

export function useBasePath(): string {
  return useBasePathContext().basePath;
}

export function useResolvePath(): (path: string) => string {
  return useBasePathContext().resolvePath;
}
